import { type ModerationReportType } from '@shared/all/domains/moderation';
import { type Product } from '@shared/all/domains/products';
import { createContext, type PropsWithChildren, useState } from 'react';

// Define the context interface
interface AppWidgetContextType {
  moderationReportType: ModerationReportType | null;
  moderationReportData: [ModerationReportType | null, string | number | null];
  purchaseModal: { isOpen: boolean; product: Product | null };
  foundersTokenModalOpen: boolean;
  newModerationReport: (
    type: ModerationReportType,
    target: string | number
  ) => void;
  closeNewModerationReportModal: () => void;

  setPurchaseModal: (isOpen: boolean, product: Product | null) => void;
  openFoundersTokenModal: () => void;
  dismissFoundersTokenModal: () => void;
}

// Default context values
const defaultContext: AppWidgetContextType = {
  moderationReportType: null,
  moderationReportData: [null, null],
  purchaseModal: { isOpen: false, product: null },
  foundersTokenModalOpen: false,
  newModerationReport: () => {},
  closeNewModerationReportModal: () => {},

  setPurchaseModal: () => {},
  openFoundersTokenModal: () => {},
  dismissFoundersTokenModal: () => {}
};

const AppWidgetContext = createContext<AppWidgetContextType>(defaultContext);

export const AppWidgetContextProvider = ({ children }: PropsWithChildren) => {
  const [moderationReportType, setModerationReportType] =
    useState<ModerationReportType | null>(null);
  const [target, setTarget] = useState<string | null | number>(null);

  const [foundersTokenModalOpen, setFoundersTokenModalOpen] = useState(false);
  const [purchaseModal, setPurchaseModal] = useState<{
    isOpen: boolean;
    product: Product | null;
  }>({
    isOpen: false,
    product: null
  });

  const contextValue: AppWidgetContextType = {
    moderationReportType,
    moderationReportData: [moderationReportType, target],
    purchaseModal,
    foundersTokenModalOpen,
    newModerationReport: (
      type: ModerationReportType,
      target: string | number
    ) => {
      setModerationReportType(type);
      setTarget(target);
    },
    closeNewModerationReportModal: () => setModerationReportType(null),
    setPurchaseModal: (isOpen: boolean, product: Product | null) =>
      setPurchaseModal({ isOpen, product }),
    openFoundersTokenModal: () => {
      setFoundersTokenModalOpen(true);
    },
    dismissFoundersTokenModal: () => {
      setFoundersTokenModalOpen(false);
    }
  };

  return (
    <AppWidgetContext.Provider value={contextValue}>
      {children}
    </AppWidgetContext.Provider>
  );
};

export default AppWidgetContext;
